/**
 * Pulse synthesis — a generated editorial READ of the wall, in large text, above
 * the Pulse. The wall is passed as `children` so the read + wall share one
 * positioned stage; the read's phrases wire to the wall's real bars (found by the
 * `data-pulse-bar` / `data-pulse-cluster` / `data-pulse-mode` tags the wall
 * already renders). The interaction is one-way — the READ drives the wall, not the
 * reverse: hover a phrase → its signals light, the rest dim, a connector curves
 * from the word to each bar. The bars themselves keep their own native hover
 * (links/navigation); hovering a bar does nothing to the read.
 * Click a phrase (or the top-right affordance) → the bottom chat bar (the app's
 * single chat surface) opens seeded with the question.
 *
 * Two entry points share one view:
 *   · `PulseSynthesis` — one mode's wall (its sections are the bands).
 *   · `PulseSynthesisView` — the raw view, fed a pre-built `Synthesis`. The home
 *     uses it directly with a workspace read + `bandSelector="[data-pulse-mode]"`
 *     (its bands are MODES). See `buildWorkspaceSynthesis`.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Icon } from "../../icon";
import { cn } from "../../utils";
import { useHydrated } from "../../use-hydrated";
import { useApi } from "../../api/client";
import { pulse } from "../../api/contracts/pulse";
import {
  buildPulseSynthesis,
  buildModeReadBrief,
  hashBrief,
  llmReadToSynthesis,
  subjectForScope,
  type Synthesis,
  type SynthSeg,
} from "./pulse-synthesis-build";
import type { Band } from "./pulse-wall-shared";

const TONE_UNDER: Record<Band, string> = {
  ok: "decoration-success/40",
  watch: "decoration-warning/50",
  risk: "decoration-danger/50",
};
const TONE_TINT: Record<Band, string> = {
  ok: "bg-success/10",
  watch: "bg-warning/10",
  risk: "bg-danger/10",
};
const TONE_TEXT: Record<Band, string> = {
  ok: "text-success",
  watch: "text-warning",
  risk: "text-danger",
};
const TONE_VAR: Record<Band, string> = {
  ok: "var(--success)",
  watch: "var(--warning)",
  risk: "var(--danger)",
};

type RInsight = Extract<SynthSeg, { t: "insight" }> & { id: string };
type RSeg = Extract<SynthSeg, { t: "text" }> | RInsight;
type Line = { d: string; sx: number; sy: number; x: number; y: number };

/** Open the bottom chat bar seeded with `query` (decoupled via the shell's
 * event — the DockBar catches `excellent:ask-agent`, prefills + focuses). */
function chatAbout(query: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("excellent:ask-agent", { detail: { query } }));
}

const esc = (s: string) =>
  typeof CSS !== "undefined" && CSS.escape ? CSS.escape(s) : s;

/** Find the wall element a ref points at: a bar, else a section, else a mode. */
function targetEl(stage: HTMLElement, ref: string): HTMLElement | null {
  return (
    stage.querySelector<HTMLElement>(`[data-pulse-bar="${esc(ref)}"]`) ??
    stage.querySelector<HTMLElement>(`[data-pulse-cluster="${esc(ref)}"]`) ??
    stage.querySelector<HTMLElement>(`[data-pulse-mode="${esc(ref)}"]`)
  );
}

/** Remove every highlight we applied to the wall (idempotent). */
function clearFx(stage: HTMLElement) {
  stage.querySelectorAll<HTMLElement>("[data-synth-dim]").forEach((el) => {
    el.style.removeProperty("opacity");
    el.removeAttribute("data-synth-dim");
  });
  stage.querySelectorAll<HTMLElement>("[data-synth-lit]").forEach((el) => {
    el.style.removeProperty("outline");
    el.style.removeProperty("outline-offset");
    el.style.removeProperty("border-radius");
    el.removeAttribute("data-synth-lit");
  });
}

// ── the view (presentation + wiring) ──────────────────────────────────────────
export interface PulseSynthesisViewProps {
  /** The pre-built read (null = render the wall only, no read). */
  synth: Synthesis;
  /** Sentence subject (for the affordance label). */
  subject: string;
  /**
   * A titled surface (a mode root or a drilled `?area` section) renders ONLY its
   * wall here — NO read. The title itself no longer renders in this component: it
   * was decoupled into the standalone, pulse-independent `<SectionTitleBar>` (Matt,
   * 2026-07-16). This prop now only SIGNALS "this is a titled surface" so the
   * home-only editorial read is suppressed. Omit for the classic full-width 1.55rem
   * read sitting on the wall (the home keeps this).
   */
  title?: string;
  /** @deprecated The eyebrow now lives on `<SectionTitleBar>`; ignored here. */
  eyebrow?: string;
  /**
   * Tighten the vertical rhythm of the titled surface's wall (a section strip vs a
   * mode-root hero). Since the title decoupled to `<SectionTitleBar>`, this no
   * longer changes the title face/size — only the wall wrapper's gap.
   */
  compact?: boolean;
  /** @deprecated The section's CTA/filters now ride `<SectionTitleBar actions>`; ignored here. */
  titleToolbar?: ReactNode;
  /** The "band" granularity the wall dims at: sections (default) or modes. */
  bandSelector?: string;
  /** Changes when the page/scope changes — re-keys the read so it re-animates in
   *  on navigation (each page brings its own read). */
  transitionKey?: string;
  /** The Pulse wall — always rendered inside the shared stage. */
  children: ReactNode;
  className?: string;
}

export function PulseSynthesisView({
  synth,
  subject,
  title,
  compact = false,
  bandSelector = "[data-pulse-cluster]",
  transitionKey,
  children,
  className,
}: PulseSynthesisViewProps) {
  const hydrated = useHydrated();

  // Assign a stable id to each insight segment; build lookup + reverse(ref→id).
  const rendered = useMemo(() => {
    if (!synth) return null;
    let i = 0;
    const segs: RSeg[] = synth.segments.map((s) =>
      s.t === "insight" ? { ...s, id: `s${i++}` } : s,
    );
    const byId = new Map<string, RInsight>();
    for (const s of segs) if (s.t === "insight") byId.set(s.id, s);
    return { segs, byId };
  }, [synth]);

  const stageRef = useRef<HTMLDivElement>(null);
  const spanRefs = useRef(new Map<string, HTMLElement>());
  const [focusId, setFocusId] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);

  const focusIns = focusId && rendered ? rendered.byId.get(focusId) : null;

  // Light the wall + draw connectors for the focused phrase.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const apply = () => {
      clearFx(stage);
      const ins = focusId && rendered ? rendered.byId.get(focusId) : null;
      if (!ins || ins.refs.includes("all")) {
        setLines([]);
        return;
      }
      const targets: HTMLElement[] = [];
      for (const ref of ins.refs) {
        const el = targetEl(stage, ref);
        if (el) targets.push(el);
      }
      if (targets.length === 0) {
        setLines([]);
        return;
      }
      const tone = TONE_VAR[ins.tone];
      // dim every band that holds none of the lit signals
      stage.querySelectorAll<HTMLElement>(bandSelector).forEach((c) => {
        if (!targets.some((t) => c === t || c.contains(t))) {
          c.style.opacity = "0.3";
          c.setAttribute("data-synth-dim", "");
        }
      });
      // ring the lit signals in their band tone (outline = no layout shift, no
      // clobber of the wall's own box-shadow)
      targets.forEach((t) => {
        t.style.outline = `2px solid ${tone}`;
        t.style.outlineOffset = "2px";
        // radius-ignore: imperative hover-outline on a lit pulse bar — the
        // `--radius-sm` (6px) token, set in JS because the outline is applied
        // transiently in a DOM effect, not via a className.
        t.style.borderRadius = "var(--radius-sm)";
        t.setAttribute("data-synth-lit", "");
      });
      // connectors from the phrase down to each lit signal
      const span = spanRefs.current.get(focusId!);
      const sr = stage.getBoundingClientRect();
      const next: Line[] = [];
      if (span) {
        const sp = span.getBoundingClientRect();
        const sx = sp.left + sp.width / 2 - sr.left;
        const sy = sp.bottom - sr.top;
        targets.forEach((t) => {
          const r = t.getBoundingClientRect();
          const bx = r.left + r.width / 2 - sr.left;
          const by = r.top - sr.top;
          const dy = by - sy;
          next.push({
            d: `M ${sx} ${sy} C ${sx} ${sy + dy * 0.45} ${bx} ${by - dy * 0.45} ${bx} ${by}`,
            sx,
            sy,
            x: bx,
            y: by,
          });
        });
      }
      setLines(next);
    };
    apply();
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      clearFx(stage);
    };
  }, [focusId, rendered, bandSelector]);

  const showRead = hydrated && !!rendered && !!synth;

  // The read's segments — the classic full-width read (the untitled layout /
  // home). The titled layout (mode homepages) shows a title-only header with no
  // read at all.
  const readSegs = rendered
    ? rendered.segs.map((s, i) =>
        s.t === "text" ? (
          <span key={i}>{s.text}</span>
        ) : (
          <Phrase
            key={i}
            seg={s}
            active={focusId === s.id}
            registerRef={(el) => {
              if (el) spanRefs.current.set(s.id, el);
              else spanRefs.current.delete(s.id);
            }}
            onEnter={() => setFocusId(s.id)}
            onLeave={() => setFocusId(null)}
          />
        ),
      )
    : null;

  // Connector overlay — click-through, drawn at the STAGE level so its origin
  // matches the coordinate space the effect measures against
  // (stage.getBoundingClientRect()). Only the classic (untitled) layout reaches
  // here — the titled layout returns a title-only header above — and there the
  // read block's top-left coincides with the stage's.
  const connectors = (
    <svg className="pointer-events-none absolute inset-0 z-20 h-full w-full overflow-visible">
      {focusIns &&
        lines.map((l, i) => (
          <g key={i} className={TONE_TEXT[focusIns.tone]}>
            <path
              d={l.d}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              opacity={0.85}
            />
            <circle cx={l.sx} cy={l.sy} r={2.5} fill="currentColor" />
            <circle cx={l.x} cy={l.y} r={3.5} fill="currentColor" />
            <circle cx={l.x} cy={l.y} r={6} fill="currentColor" opacity={0.18} />
          </g>
        ))}
    </svg>
  );

  if (title) {
    // Title DECOUPLED from the Pulse (Matt, 2026-07-16): the scope's name + its
    // injected actions now render in the standalone, pulse-INDEPENDENT
    // `<SectionTitleBar>` (composed by the shell above this wall), so a page's
    // title/CTA survive even when the Pulse is disabled. A titled surface therefore
    // renders ONLY its wall here — no title, no editorial read. `title` stays as the
    // signal that this IS a titled surface (→ suppress the home-only read below).
    return (
      <div className={cn("relative flex flex-col", compact ? "gap-4" : "gap-7", className)}>
        {children}
      </div>
    );
  }

  return (
    <div ref={stageRef} className={cn("relative flex flex-col gap-7", className)}>
      {connectors}
      {showRead && (
        <div className="group/synthesis relative">
          <p
            // Re-key on the page/scope so the read re-animates in on navigation
            // — it materializes (fade + rise + de-blur) as the new page's insight.
            key={transitionKey}
            // The read stretches the FULL width of the well (right edge aligns to
            // the wall below it) — no measure cap, no right padding for the chat
            // affordance (which now lives OUTSIDE the well, in the right gutter).
            // It sits above the connector wires (z-30 over the z-20 svg) so glyphs
            // stay crisp; a faint same-color scrim + backdrop blur softens any wire
            // passing behind the text WITHOUT reading as a panel on the flat page
            // background (bg-over-bg composites away; blurring flat = no-op).
            className="animate-synth-read relative z-30 rounded-lg bg-background/30 type-heading font-medium tracking-[-0.01em] text-justify hyphens-auto text-muted-foreground backdrop-blur-[3px] [&_span]:align-baseline"
          >
            {readSegs}
          </p>

          {/* chat affordance — sits OUTSIDE the well, in the right gutter (the rail
              reserve), pinned just past the read's right edge and aligned to its
              first line. `left-full` anchors the icon flush to the gutter; the label
              reveals rightward on hover so it never crowds the full-width read.
              button-ignore: a custom faint→bright "chat about this" affordance with
              a fading label; no variant has a faded-by-default treatment. */}
          <button
            type="button"
            aria-label={`Chat about ${subject}`}
            onClick={() => synth && chatAbout(synth.pageChat)}
            className="absolute left-full top-1 z-30 ml-3 flex items-center gap-1.5 rounded-md px-1.5 py-1 text-muted-foreground/40 outline-none transition-colors hover:text-foreground focus-visible:text-foreground group-hover/synthesis:text-muted-foreground"
          >
            <Icon name="message-square" size="sm" />
            <span className="type-subtext opacity-0 transition-opacity group-hover/synthesis:opacity-100">
              Ask
            </span>
          </button>
        </div>
      )}
      {children}
    </div>
  );
}

// ── per-mode wrapper (fetches one scope) ──────────────────────────────────────
export interface PulseSynthesisProps {
  /** Same scope the wall below uses (`"work"`, `"<mode>"`). */
  scope: string;
  /** Keeps the read scoped to a project tier; forwarded to match the wall's key. */
  projectSlug?: string;
  /** Sentence subject override (else derived from `scope`). */
  subject?: string;
  /** A band `key` to narrow the read to — a sub-page drilled INTO one band reads
   *  about that band's signals ("insight into this page"), not the whole mode.
   *  Omit on a mode root for the cross-band read. */
  focus?: string | null;
  /** Title-only header above the wall (no read) — see PulseSynthesisViewProps. */
  title?: string;
  /** The tiny letterspaced eyebrow under the title (e.g. the mode's question). */
  eyebrow?: string;
  /** Step the titled header down to the compact `<PageHeader>` treatment — set on
   *  a SECTION / drilled scope; editorial stays on the six mode roots. See
   *  PulseSynthesisViewProps.compact (UI-UX-PATTERNS-PRD Issue 7). */
  compact?: boolean;
  /** The section's primary CTA + filters, inline on the compact title row. */
  titleToolbar?: ReactNode;
  /** The Pulse wall — always rendered inside the shared stage. */
  children: ReactNode;
  className?: string;
}

export function PulseSynthesis({
  scope,
  projectSlug,
  subject,
  focus,
  title,
  eyebrow,
  compact,
  titleToolbar,
  children,
  className,
}: PulseSynthesisProps) {
  const subj = subject ?? subjectForScope(scope);

  // Altitude: a mode-root read (no focus) uses section-rollup so it hashes
  // IDENTICALLY to the home's pre-warm of this mode (one generation serves both);
  // a focused band read needs the per-touchpoint wall to read its signals.
  const { data } = useApi(
    pulse.aggregate,
    {
      params: { scope },
      query: {
        projectSlug: projectSlug ?? undefined,
        ...(focus ? {} : { rollup: "sections" as const }),
      },
    },
    { keepPreviousData: true },
  );

  // The deterministic read — renders INSTANTLY and is the guaranteed floor.
  const formulaSynth = useMemo(() => {
    if (!data || data.overall == null) return null;
    return buildPulseSynthesis(data.groups ?? [], data.overall, {
      subject: subj,
      focus: focus ?? null,
    });
  }, [data, subj, focus]);

  // The LLM read — generated server-side via the local `claude` CLI, cached by
  // brief-hash (so a changed pulse auto-regenerates), layered on once warm.
  const brief = useMemo(
    () =>
      data && data.overall != null && (data.groups?.length ?? 0) > 0
        ? buildModeReadBrief(data.groups ?? [], data.overall, {
            subject: subjectForScope(scope),
            scope,
            focus: focus ?? null,
          })
        : null,
    [data, scope, focus],
  );
  const briefHash = useMemo(
    () => (brief && brief.groups.length > 0 ? hashBrief(brief) : null),
    [brief],
  );
  const { data: llm } = useApi(
    pulse.synthesisRead,
    briefHash && brief ? { query: { h: briefHash }, body: brief } : null,
    { revalidateOnFocus: false, revalidateIfStale: false, shouldRetryOnError: false, keepPreviousData: true },
  );

  const synth = useMemo(() => {
    if (llm?.read && brief) {
      return llmReadToSynthesis(
        llm.read,
        brief,
        formulaSynth?.pageChat ??
          `Give me the read on ${subj.toLowerCase()} — what's the most important thing to do?`,
      );
    }
    return formulaSynth;
  }, [llm, brief, formulaSynth, subj]);

  return (
    <PulseSynthesisView
      synth={synth}
      subject={subj}
      title={title}
      eyebrow={eyebrow}
      compact={compact}
      titleToolbar={titleToolbar}
      // Re-key on scope + focus (and swap to the LLM read) so it re-animates when
      // you navigate to another page or the richer read arrives.
      transitionKey={llm?.read ? `llm:${briefHash}` : `${scope}:${focus ?? ""}`}
      className={className}
    >
      {children}
    </PulseSynthesisView>
  );
}

function Phrase({
  seg,
  active,
  registerRef,
  onEnter,
  onLeave,
}: {
  seg: RInsight;
  active: boolean;
  registerRef: (el: HTMLElement | null) => void;
  onEnter: () => void;
  onLeave: () => void;
}) {
  return (
    // surface-ignore: inline insight wired to the wall + the bottom chat bar —
    // there's no atom for an interactive inline span that lights a chart on hover.
    <span
      ref={registerRef}
      data-synth-phrase=""
      role="button"
      tabIndex={0}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      onClick={() => chatAbout(seg.chat)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          chatAbout(seg.chat);
        }
      }}
      className={cn(
        "cursor-pointer rounded-xs px-0.5 -mx-0.5 text-foreground underline decoration-dotted decoration-2 underline-offset-[5px] outline-none transition-colors hover:decoration-solid",
        TONE_UNDER[seg.tone],
        active && cn("decoration-solid", TONE_TINT[seg.tone]),
      )}
    >
      {seg.text}
    </span>
  );
}
